import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../service/Api';
import { Loader2, Building2, UserPlus, Users, Edit, LogIn, Plus, Search, ChevronLeft, ChevronRight, Trash2, Layers, BarChart2 } from 'lucide-react';
import Drawer from '../../components/common/Drawer';
import toast from 'react-hot-toast';
import { useCrmAnalysis } from '../../context/CrmAnalysisContext';

// Helper to get base URL for fetch calls
const getBaseUrl = () => {
  return apiService.api.defaults.baseURL || process.env.REACT_APP_API_URL || '';
};

export default function AdminDashboard() {
  const navigate                           = useNavigate();
  const { setSelectedTenant }              = useCrmAnalysis();
  const [activeTab, setActiveTab] = useState('tenants'); // 'tenants' or 'users'
  const [loading, setLoading] = useState(true);
  const [tenants, setTenants] = useState([]);
  const [users, setUsers] = useState([]);
  
  // Pagination state for tenants
  const [tenantsPagination, setTenantsPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 1,
    hasNextPage: false,
    hasPreviousPage: false
  });
  
  // Pagination state for users
  const [usersPagination, setUsersPagination] = useState({
    page: 1,
    pageSize: 10,
    total: 0,
    totalPages: 1,
    hasNextPage: false,
    hasPreviousPage: false
  });
  
  // Search state
  const [tenantsSearch, setTenantsSearch] = useState('');
  const [usersSearch, setUsersSearch] = useState('');
  
  // Delete confirmation state
  const [confirmDeleteTenant, setConfirmDeleteTenant] = useState(null);
  const [confirmDeleteUser, setConfirmDeleteUser] = useState(null);
  const [deletingTenant, setDeletingTenant] = useState(false);
  const [deletingUser, setDeletingUser] = useState(false);
  
  // Tenant form state
  const [showTenantForm, setShowTenantForm] = useState(false);
  const [tenantFormData, setTenantFormData] = useState({
    name: '',
    domain: ''
  });
  const [creatingTenant, setCreatingTenant] = useState(false);
  
  // User form state
  const [showUserForm, setShowUserForm] = useState(false);
  const [userFormData, setUserFormData] = useState({
    name: '',
    email: '',
    no_of_allowed_collaterals: '',
    no_of_edit_per_collateral: '',
    tenant_id: '',
    role: 'admin'
  });
  const [creatingUser, setCreatingUser] = useState(false);
  const [updatingUser, setUpdatingUser] = useState(false);
  
  // Edit user state
  const [editingUser, setEditingUser] = useState(null);

  // Features state
  const [features, setFeatures] = useState([]);
  const [showFeatureForm, setShowFeatureForm] = useState(false);
  const [featureFormData, setFeatureFormData] = useState({ title: '', description: '' });
  const [creatingFeature, setCreatingFeature] = useState(false);
  const [editingFeature, setEditingFeature] = useState(null);
  const [confirmDeleteFeature, setConfirmDeleteFeature] = useState(null);
  const [deletingFeature, setDeletingFeature] = useState(false);

  // Collateral Types state
  const [collateralTypes, setCollateralTypes] = useState([]);
  const [showCollateralTypeForm, setShowCollateralTypeForm] = useState(false);
  const [collateralTypeFormData, setCollateralTypeFormData] = useState({ title: '', description: '' });
  const [creatingCollateralType, setCreatingCollateralType] = useState(false);
  const [editingCollateralType, setEditingCollateralType] = useState(null);
  const [confirmDeleteCollateralType, setConfirmDeleteCollateralType] = useState(null);
  const [deletingCollateralType, setDeletingCollateralType] = useState(false);

  // Offerings sub-tab state
  const [offeringsSubTab, setOfferingsSubTab] = useState('features'); // 'features' or 'collateral-types'

  useEffect(() => {
    loadData();
  }, []);

  // Load features
  const loadFeatures = useCallback(async () => {
    try {
      const response = await apiService.getCollateralFeatures();
      if (response.data?.success) {
        setFeatures(response.data.data || []);
      }
    } catch (error) {
      console.error('Error loading features:', error);
      toast.error('Failed to load features');
    }
  }, []);

  // Load collateral types
  const loadCollateralTypes = useCallback(async () => {
    try {
      const response = await apiService.getCollateralTypes();
      if (response.data?.success) {
        setCollateralTypes(response.data.data || []);
      }
    } catch (error) {
      console.error('Error loading collateral types:', error);
      toast.error('Failed to load collateral types');
    }
  }, []);

  // Load tenants with pagination and search
  const loadTenants = useCallback(async (page = 1, search = '') => {
    try {
      const response = await apiService.getTenantsPaginated({
        page,
        pageSize: 10,
        search
      });

      if (response.data?.success) {
        setTenants(response.data.data || []);
        if (response.data.pagination) {
          setTenantsPagination(response.data.pagination);
        }
      }
    } catch (error) {
      console.error('Error loading tenants:', error);
      toast.error('Failed to load tenants');
    }
  }, []);

  // Load users with pagination and search
  const loadUsers = useCallback(async (page = 1, search = '') => {
    try {
      const response = await apiService.getUsersPaginated({
        page,
        pageSize: 10,
        search
      });

      if (response.data?.success) {
        setUsers(response.data.data || []);
        if (response.data.pagination) {
          setUsersPagination(response.data.pagination);
        }
      }
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Failed to load users');
    }
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadTenants(1, tenantsSearch),
        loadUsers(1, usersSearch),
        loadFeatures(),
        loadCollateralTypes()
      ]);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Handle search with debounce and reset to page 1
  useEffect(() => {
    const timer = setTimeout(() => {
      if (activeTab === 'tenants') {
        setTenantsPagination(prev => ({ ...prev, page: 1 }));
        loadTenants(1, tenantsSearch);
      } else if (activeTab === 'users') {
        setUsersPagination(prev => ({ ...prev, page: 1 }));
        loadUsers(1, usersSearch);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [tenantsSearch, usersSearch, activeTab, loadTenants, loadUsers]);

  const handleCreateTenant = async (e) => {
    e.preventDefault();
    if (creatingTenant) return;

    const name = tenantFormData.name.trim();
    let domain = tenantFormData.domain.trim().toLowerCase();

    if (!name || !domain) {
      toast.error('Tenant name and domain are required');
      return;
    }

    // Strip protocol, www, and path — keep only the domain
    domain = domain.replace(/^https?:\/\//i, '').replace(/^www\./i, '').split('/')[0].split('?')[0];

    // Validate domain format
    if (!/^[a-z0-9]([a-z0-9\-\.]*[a-z0-9])?(\.[a-z]{2,})$/.test(domain)) {
      toast.error(`Invalid domain format. Use something like "example.com"`);
      return;
    }

    setCreatingTenant(true);
    try {
      const response = await apiService.createTenant({ name, domain });
      if (response.data?.success) {
        toast.success('Tenant created successfully');
        setShowTenantForm(false);
        setTenantFormData({ name: '', domain: '' });
        loadTenants(tenantsPagination.page, tenantsSearch);
      } else {
        toast.error(response.data?.message || 'Failed to create tenant');
      }
    } catch (error) {
      console.error('Error creating tenant:', error);
      toast.error(error.response?.data?.message || 'Failed to create tenant');
    } finally {
      setCreatingTenant(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (creatingUser) return;

    const { name, email, tenant_id, no_of_allowed_collaterals, no_of_edit_per_collateral, role } = userFormData;

    if (!name || !email || !tenant_id || !no_of_allowed_collaterals || !no_of_edit_per_collateral || !role) {
      toast.error('All fields are required');
      return;
    }

    setCreatingUser(true);
    try {
      const userData = {
        name: name,
        email: email,
        no_of_allowed_collaterals: no_of_allowed_collaterals,
        no_of_edit_per_collateral: no_of_edit_per_collateral,
        role: role
      };
      const response = await apiService.createUser(userData, tenant_id);
      if (response.data?.message) {
        toast.success('User created successfully');
        setShowUserForm(false);
        setUserFormData({
          name: '',
          email: '',
          no_of_allowed_collaterals: '',
          no_of_edit_per_collateral: '',
          tenant_id: '',
          role: 'admin'
        });
        loadUsers(usersPagination.page, usersSearch);
      } else {
        toast.error(response.data?.error || 'Failed to create user');
      }
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error(error.response?.data?.error || error.response?.data?.message || 'Failed to create user');
    } finally {
      setCreatingUser(false);
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (updatingUser) return;

    setUpdatingUser(true);
    try {
      const response = await apiService.updateUser(userFormData);
      if (response.data?.message) {
        toast.success('User updated successfully');
        setEditingUser(null);
        setShowUserForm(false);
        setUserFormData({
          name: '',
          email: '',
          no_of_allowed_collaterals: '',
          no_of_edit_per_collateral: '',
          tenant_id: '',
          role: 'admin'
        });
        loadUsers(usersPagination.page, usersSearch);
      } else {
        toast.error(response.data?.error || 'Failed to update user');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error(error.response?.data?.error || error.response?.data?.message || 'Failed to update user');
    } finally {
      setUpdatingUser(false);
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setUserFormData({
      id: user.id,
      name: user.name || '',
      email: user.email || '',
      no_of_allowed_collaterals: user.no_of_allowed_collaterals || '',
      no_of_edit_per_collateral: user.no_of_edit_per_collateral || '',
      role: user.role || 'admin',
    });
    setShowUserForm(true);
  };

  const handleCreateFeature = async (e) => {
    e.preventDefault();
    if (creatingFeature) return;

    const title = featureFormData.title.trim();
    const description = featureFormData.description.trim();

    if (!title || !description) {
      toast.error('Title and description are required');
      return;
    }

    setCreatingFeature(true);
    try {
      const response = await apiService.createCollateralFeature({ title, description });
      if (response.data?.success) {
        toast.success('Feature created successfully');
        setShowFeatureForm(false);
        setFeatureFormData({ title: '', description: '' });
        loadFeatures();
      } else {
        toast.error(response.data?.message || 'Failed to create feature');
      }
    } catch (error) {
      console.error('Error creating feature:', error);
      toast.error(error.response?.data?.message || 'Failed to create feature');
    } finally {
      setCreatingFeature(false);
    }
  };

  const handleUpdateFeature = async (e) => {
    e.preventDefault();
    if (creatingFeature) return;

    const title = featureFormData.title.trim();
    const description = featureFormData.description.trim();

    if (!title || !description) {
      toast.error('Title and description are required');
      return;
    }

    setCreatingFeature(true);
    try {
      const response = await apiService.updateCollateralFeature(editingFeature.id, { title, description });
      if (response.data?.success) {
        toast.success('Feature updated successfully');
        setShowFeatureForm(false);
        setEditingFeature(null);
        setFeatureFormData({ title: '', description: '' });
        loadFeatures();
      } else {
        toast.error(response.data?.message || 'Failed to update feature');
      }
    } catch (error) {
      console.error('Error updating feature:', error);
      toast.error(error.response?.data?.message || 'Failed to update feature');
    } finally {
      setCreatingFeature(false);
    }
  };

  const handleDeleteFeature = async (featureId) => {
    if (deletingFeature) return;
    
    setDeletingFeature(true);
    try {
      const response = await apiService.deleteCollateralFeature(featureId);
      if (response.data?.success) {
        toast.success('Feature deleted successfully');
        setConfirmDeleteFeature(null);
        loadFeatures();
      } else {
        toast.error(response.data?.message || 'Failed to delete feature');
      }
    } catch (error) {
      console.error('Error deleting feature:', error);
      toast.error(error.response?.data?.message || 'Failed to delete feature');
    } finally {
      setDeletingFeature(false);
    }
  };

  const handleEditFeature = (feature) => {
    setEditingFeature(feature);
    setFeatureFormData({
      title: feature.value.title || '',
      description: feature.value.description || ''
    });
    setShowFeatureForm(true);
  };

  // Collateral Type handlers
  const handleCreateCollateralType = async (e) => {
    e.preventDefault();
    if (creatingCollateralType) return;

    const title = collateralTypeFormData.title.trim();
    const description = collateralTypeFormData.description.trim();

    if (!title || !description) {
      toast.error('Title and description are required');
      return;
    }

    setCreatingCollateralType(true);
    try {
      const response = await apiService.createCollateralType({ title, description });
      if (response.data?.success) {
        toast.success('Collateral type created successfully');
        setShowCollateralTypeForm(false);
        setCollateralTypeFormData({ title: '', description: '' });
        loadCollateralTypes();
      } else {
        toast.error(response.data?.message || 'Failed to create collateral type');
      }
    } catch (error) {
      console.error('Error creating collateral type:', error);
      toast.error(error.response?.data?.message || 'Failed to create collateral type');
    } finally {
      setCreatingCollateralType(false);
    }
  };

  const handleUpdateCollateralType = async (e) => {
    e.preventDefault();
    if (creatingCollateralType) return;

    const title = collateralTypeFormData.title.trim();
    const description = collateralTypeFormData.description.trim();

    if (!title || !description) {
      toast.error('Title and description are required');
      return;
    }

    setCreatingCollateralType(true);
    try {
      const response = await apiService.updateCollateralType(editingCollateralType.id, { title, description });
      if (response.data?.success) {
        toast.success('Collateral type updated successfully');
        setShowCollateralTypeForm(false);
        setEditingCollateralType(null);
        setCollateralTypeFormData({ title: '', description: '' });
        loadCollateralTypes();
      } else {
        toast.error(response.data?.message || 'Failed to update collateral type');
      }
    } catch (error) {
      console.error('Error updating collateral type:', error);
      toast.error(error.response?.data?.message || 'Failed to update collateral type');
    } finally {
      setCreatingCollateralType(false);
    }
  };

  const handleDeleteCollateralType = async (collateralTypeId) => {
    if (deletingCollateralType) return;
    
    setDeletingCollateralType(true);
    try {
      const response = await apiService.deleteCollateralType(collateralTypeId);
      if (response.data?.success) {
        toast.success('Collateral type deleted successfully');
        setConfirmDeleteCollateralType(null);
        loadCollateralTypes();
      } else {
        toast.error(response.data?.message || 'Failed to delete collateral type');
      }
    } catch (error) {
      console.error('Error deleting collateral type:', error);
      toast.error(error.response?.data?.message || 'Failed to delete collateral type');
    } finally {
      setDeletingCollateralType(false);
    }
  };

  const handleEditCollateralType = (collateralType) => {
    setEditingCollateralType(collateralType);
    setCollateralTypeFormData({
      title: collateralType.value.title || '',
      description: collateralType.value.description || ''
    });
    setShowCollateralTypeForm(true);
  };

  const handleDeleteTenant = async (tenantId) => {
    if (deletingTenant) return;
    
    setDeletingTenant(true);
    try {
      const response = await apiService.deleteTenant(tenantId);
      if (response.data?.success) {
        toast.success(response.data.message || 'Tenant deleted successfully');
        setConfirmDeleteTenant(null);
        loadTenants(tenantsPagination.page, tenantsSearch);
      } else {
        toast.error(response.data?.message || 'Failed to delete tenant');
      }
    } catch (error) {
      console.error('Error deleting tenant:', error);
      toast.error(error.response?.data?.message || 'Failed to delete tenant');
    } finally {
      setDeletingTenant(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (deletingUser) return;
    
    setDeletingUser(true);
    try {
      const response = await apiService.deleteUser(userId);
      if (response.data?.success) {
        toast.success(response.data.message || 'User deleted successfully');
        setConfirmDeleteUser(null);
        loadUsers(usersPagination.page, usersSearch);
      } else {
        toast.error(response.data?.message || 'Failed to delete user');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(error.response?.data?.message || 'Failed to delete user');
    } finally {
      setDeletingUser(false);
    }
  };

  const handleProxyLogin = async (userId) => {
    try {
      // Save current admin credentials before proxy login
      const currentToken = localStorage.getItem('aura_token');
      const currentTenantId = localStorage.getItem('aura_tenant_id');
      
      if (currentToken && currentTenantId) {
        localStorage.setItem('aura_admin_token_backup', currentToken);
        localStorage.setItem('aura_admin_tenant_id_backup', currentTenantId);
        console.log('Admin credentials saved for restoration after logout');
      }

      const response = await apiService.proxyLogin(userId);
      
      if (response.data?.success && response.data?.redirectUrl) {
        // Navigate to the redirect URL in the same tab
        window.location.href = response.data.redirectUrl;
      } else {
        toast.error(response.data?.error || 'Failed to get redirect URL');
      }
    } catch (error) {
      console.error('Error with proxy login:', error);
      
      if (error.response?.data) {
        if (error.response.data.redirectUrl) {
          window.location.href = error.response.data.redirectUrl;
        } else {
          toast.error(error.response.data.error || 'Failed to proxy login');
        }
      } else {
        toast.error(error.message || 'Failed to proxy login');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" style={{ backgroundColor: '#F8F7F4' }}>
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" style={{ color: '#FF6B5A' }} />
          <p style={{ color: '#0A0A10' }}>Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F8F7F4' }}>
      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="mb-12">
          <h1 className="text-4xl font-bold mb-2" style={{ color: '#0A0A10' }}>
            Admin Dashboard
          </h1>
          <p className="text-xl" style={{ color: '#0A0A10', opacity: 0.7 }}>
            Manage tenants and users
          </p>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-8 border-b" style={{ borderColor: '#D7B472' }}>
          <button
            onClick={() => setActiveTab('tenants')}
            className={`px-6 py-3 font-semibold transition-colors ${
              activeTab === 'tenants'
                ? 'border-b-2'
                : 'opacity-70 hover:opacity-100'
            }`}
            style={{
              color: '#0A0A10',
              borderBottomColor: activeTab === 'tenants' ? '#FF6B5A' : 'transparent'
            }}
          >
            Tenants ({tenantsPagination.total || tenants.length})
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`px-6 py-3 font-semibold transition-colors ${
              activeTab === 'users'
                ? 'border-b-2'
                : 'opacity-70 hover:opacity-100'
            }`}
            style={{
              color: '#0A0A10',
              borderBottomColor: activeTab === 'users' ? '#FF6B5A' : 'transparent'
            }}
          >
            Users ({usersPagination.total || users.length})
          </button>
          <button
            onClick={() => setActiveTab('offerings')}
            className={`px-6 py-3 font-semibold transition-colors ${
              activeTab === 'offerings'
                ? 'border-b-2'
                : 'opacity-70 hover:opacity-100'
            }`}
            style={{
              color: '#0A0A10',
              borderBottomColor: activeTab === 'offerings' ? '#FF6B5A' : 'transparent'
            }}
          >
            Offerings ({features.length + collateralTypes.length})
          </button>
        </div>

        {/* Tenants Tab */}
        {activeTab === 'tenants' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold" style={{ color: '#0A0A10' }}>Tenants</h2>
              <button
                onClick={() => setShowTenantForm(true)}
                className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-colors"
                style={{
                  backgroundColor: '#FF6B5A',
                  color: '#FFFFFF'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#ff5540'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#FF6B5A'}
              >
                <Plus className="w-4 h-4" />
                Create Tenant
              </button>
            </div>

            {/* Search Bar */}
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4" style={{ color: '#0A0A10', opacity: 0.5 }} />
                <input
                  type="text"
                  placeholder="Search tenants by name or domain..."
                  value={tenantsSearch}
                  onChange={(e) => setTenantsSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 rounded-lg border"
                  style={{ borderColor: '#D7B472', backgroundColor: '#FFFFFF', color: '#0A0A10' }}
                />
              </div>
            </div>

            {/* Tenants Table */}
            <div className="rounded-xl shadow-sm border overflow-hidden" style={{ borderColor: '#D7B472', backgroundColor: '#FFFFFF' }}>
              <table className="w-full">
                <thead style={{ backgroundColor: '#F8F7F4' }}>
                  <tr>
                    <th className="px-6 py-4 text-left font-semibold" style={{ color: '#0A0A10' }}>ID</th>
                    <th className="px-6 py-4 text-left font-semibold" style={{ color: '#0A0A10' }}>Name</th>
                    <th className="px-6 py-4 text-left font-semibold" style={{ color: '#0A0A10' }}>Domain</th>
                    <th className="px-6 py-4 text-left font-semibold" style={{ color: '#0A0A10' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {tenants.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="px-6 py-8 text-center" style={{ color: '#0A0A10', opacity: 0.7 }}>
                        No tenants found
                      </td>
                    </tr>
                  ) : (
                    tenants.map((tenant) => (
                      <tr key={tenant.id} className="border-t" style={{ borderColor: '#D7B472' }}>
                        <td className="px-6 py-4" style={{ color: '#0A0A10' }}>{tenant.id}</td>
                        <td className="px-6 py-4" style={{ color: '#0A0A10' }}>{tenant.name}</td>
                        <td className="px-6 py-4" style={{ color: '#0A0A10' }}>{tenant.domain}</td>
                        <td className="px-6 py-4">
                          {confirmDeleteTenant === tenant.id ? (
                            <div className="flex flex-col gap-2">
                              <div className="text-xs p-2 rounded" style={{ backgroundColor: 'rgba(255, 107, 90, 0.1)', color: '#FF6B5A' }}>
                                This will delete the tenant and all associated users. This action cannot be undone.
                              </div>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => handleDeleteTenant(tenant.id)}
                                  disabled={deletingTenant}
                                  className="px-3 py-1.5 text-sm rounded transition-colors disabled:opacity-50"
                                  style={{ backgroundColor: '#FF6B5A', color: 'white' }}
                                >
                                  {deletingTenant ? (
                                    <>
                                      <Loader2 className="w-3 h-3 inline animate-spin mr-1" />
                                      Deleting...
                                    </>
                                  ) : (
                                    'Confirm Delete'
                                  )}
                                </button>
                                <button
                                  onClick={() => setConfirmDeleteTenant(null)}
                                  disabled={deletingTenant}
                                  className="px-3 py-1.5 text-sm rounded border transition-colors disabled:opacity-50"
                                  style={{ borderColor: '#D7B472', color: '#0A0A10' }}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              {/* CRM Analysis CTA */}
                              <button
                                onClick={() => {
                                  setSelectedTenant({ id: tenant.id, name: tenant.name, domain: tenant.domain });
                                  const slug = tenant.domain || tenant.name || String(tenant.id);
                                  navigate(`/crmanalysis/${encodeURIComponent(slug)}/dataSync`, {
                                    state: { tenantId: tenant.id, tenantName: tenant.name, tenantDomain: tenant.domain },
                                  });
                                }}
                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all hover:opacity-80"
                                style={{ borderColor: '#D7B472', color: '#8B6914', backgroundColor: 'rgba(215,180,114,0.08)' }}
                                title="Open CRM Analysis for this tenant"
                              >
                                <BarChart2 className="w-3.5 h-3.5" />
                                CRM Analysis
                              </button>

                              {/* Delete */}
                              <button
                                onClick={() => setConfirmDeleteTenant(tenant.id)}
                                className="p-1 rounded hover:bg-gray-200 transition-colors"
                                title="Delete tenant (will also delete all associated users)"
                              >
                                <Trash2 className="w-4 h-4" style={{ color: '#FF6B5A' }} />
                              </button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls for Tenants */}
            {tenantsPagination.totalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <div className="text-sm" style={{ color: '#0A0A10', opacity: 0.7 }}>
                  Showing {((tenantsPagination.page - 1) * tenantsPagination.pageSize) + 1} to {Math.min(tenantsPagination.page * tenantsPagination.pageSize, tenantsPagination.total)} of {tenantsPagination.total} tenants
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => loadTenants(tenantsPagination.page - 1, tenantsSearch)}
                    disabled={!tenantsPagination.hasPreviousPage}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg border transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ borderColor: '#D7B472', color: '#0A0A10' }}
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Previous
                  </button>
                  <span className="px-3 py-1.5 text-sm" style={{ color: '#0A0A10' }}>
                    Page {tenantsPagination.page} of {tenantsPagination.totalPages}
                  </span>
                  <button
                    onClick={() => loadTenants(tenantsPagination.page + 1, tenantsSearch)}
                    disabled={!tenantsPagination.hasNextPage}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg border transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ borderColor: '#D7B472', color: '#0A0A10' }}
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold" style={{ color: '#0A0A10' }}>Users</h2>
              <button
                onClick={() => {
                  setEditingUser(null);
                  setUserFormData({
                    name: '',
                    email: '',
                    no_of_allowed_collaterals: '',
                    no_of_edit_per_collateral: '',
                    tenant_id: '',
                    role: 'admin'
                  });
                  setShowUserForm(true);
                }}
                className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-colors"
                style={{
                  backgroundColor: '#FF6B5A',
                  color: '#FFFFFF'
                }}
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#ff5540'}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#FF6B5A'}
              >
                <UserPlus className="w-4 h-4" />
                Create User
              </button>
            </div>

            {/* Search Bar */}
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4" style={{ color: '#0A0A10', opacity: 0.5 }} />
                <input
                  type="text"
                  placeholder="Search users by name or email..."
                  value={usersSearch}
                  onChange={(e) => setUsersSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 rounded-lg border"
                  style={{ borderColor: '#D7B472', backgroundColor: '#FFFFFF', color: '#0A0A10' }}
                />
              </div>
            </div>

            {/* Users Table */}
            <div className="rounded-xl shadow-sm border overflow-hidden" style={{ borderColor: '#D7B472', backgroundColor: '#FFFFFF' }}>
              <table className="w-full">
                <thead style={{ backgroundColor: '#F8F7F4' }}>
                  <tr>
                    <th className="px-6 py-4 text-left font-semibold" style={{ color: '#0A0A10' }}>ID</th>
                    <th className="px-6 py-4 text-left font-semibold" style={{ color: '#0A0A10' }}>Name</th>
                    <th className="px-6 py-4 text-left font-semibold" style={{ color: '#0A0A10' }}>Email</th>
                   <th className="px-6 py-4 text-left font-semibold" style={{ color: '#0A0A10' }}>Allowed Collaterals</th>
                    <th className="px-6 py-4 text-left font-semibold" style={{ color: '#0A0A10' }}>Edits per Collateral</th>
                    <th className="px-6 py-4 text-left font-semibold" style={{ color: '#0A0A10' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="px-6 py-8 text-center" style={{ color: '#0A0A10', opacity: 0.7 }}>
                        No users found
                      </td>
                    </tr>
                  ) : (
                    users.map((user) => (
                      <tr key={user.id} className="border-t" style={{ borderColor: '#D7B472' }}>
                        <td className="px-6 py-4" style={{ color: '#0A0A10' }}>{user.id}</td>
                        <td className="px-6 py-4" style={{ color: '#0A0A10' }}>{user.name}</td>
                        <td className="px-6 py-4" style={{ color: '#0A0A10' }}>{user.email}</td>
                      
                        <td className="px-6 py-4" style={{ color: '#0A0A10' }}>{user.no_of_allowed_collaterals || '-'}</td>
                        <td className="px-6 py-4" style={{ color: '#0A0A10' }}>{user.no_of_edit_per_collateral || '-'}</td>
                        <td className="px-6 py-4">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEditUser(user)}
                              className="flex items-center gap-1 px-3 py-1.5 rounded transition-colors"
                              style={{
                                backgroundColor: 'rgba(215, 180, 114, 0.15)',
                                color: '#D7B472'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = 'rgba(215, 180, 114, 0.25)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'rgba(215, 180, 114, 0.15)';
                              }}
                            >
                              <Edit className="w-4 h-4" />
                              Edit
                            </button>
                            <button
                              onClick={() => handleProxyLogin(user.id)}
                              className="flex items-center gap-1 px-3 py-1.5 rounded transition-colors"
                              style={{
                                backgroundColor: 'rgba(255, 107, 90, 0.15)',
                                color: '#FF6B5A'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = 'rgba(255, 107, 90, 0.25)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'rgba(255, 107, 90, 0.15)';
                              }}
                            >
                              <LogIn className="w-4 h-4" />
                              Proxy Login
                            </button>
                            {confirmDeleteUser === user.id ? (
                              <div className="flex flex-col gap-2">
                                <div className="text-xs p-2 rounded" style={{ backgroundColor: 'rgba(255, 107, 90, 0.1)', color: '#FF6B5A' }}>
                                  This action cannot be undone.
                                </div>
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => handleDeleteUser(user.id)}
                                    disabled={deletingUser}
                                    className="px-3 py-1.5 text-sm rounded transition-colors disabled:opacity-50"
                                    style={{ backgroundColor: '#FF6B5A', color: 'white' }}
                                  >
                                    {deletingUser ? (
                                      <>
                                        <Loader2 className="w-3 h-3 inline animate-spin mr-1" />
                                        Deleting...
                                      </>
                                    ) : (
                                      'Confirm Delete'
                                    )}
                                  </button>
                                  <button
                                    onClick={() => setConfirmDeleteUser(null)}
                                    disabled={deletingUser}
                                    className="px-3 py-1.5 text-sm rounded border transition-colors disabled:opacity-50"
                                    style={{ borderColor: '#D7B472', color: '#0A0A10' }}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <button
                                onClick={() => setConfirmDeleteUser(user.id)}
                                className="p-1 rounded hover:bg-gray-200 transition-colors"
                                title="Delete user"
                              >
                                <Trash2 className="w-4 h-4" style={{ color: '#FF6B5A' }} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls for Users */}
            {usersPagination.totalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <div className="text-sm" style={{ color: '#0A0A10', opacity: 0.7 }}>
                  Showing {((usersPagination.page - 1) * usersPagination.pageSize) + 1} to {Math.min(usersPagination.page * usersPagination.pageSize, usersPagination.total)} of {usersPagination.total} users
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => loadUsers(usersPagination.page - 1, usersSearch)}
                    disabled={!usersPagination.hasPreviousPage}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg border transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ borderColor: '#D7B472', color: '#0A0A10' }}
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Previous
                  </button>
                  <span className="px-3 py-1.5 text-sm" style={{ color: '#0A0A10' }}>
                    Page {usersPagination.page} of {usersPagination.totalPages}
                  </span>
                  <button
                    onClick={() => loadUsers(usersPagination.page + 1, usersSearch)}
                    disabled={!usersPagination.hasNextPage}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg border transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    style={{ borderColor: '#D7B472', color: '#0A0A10' }}
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Offerings Tab */}
        {activeTab === 'offerings' && (
          <div>
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold" style={{ color: '#0A0A10' }}>Offerings</h2>
            </div>

            {/* Sub-tabs for Features and Collateral Types */}
            <div className="flex gap-4 mb-6 border-b" style={{ borderColor: '#D7B472' }}>
              <button
                onClick={() => setOfferingsSubTab('features')}
                className={`px-4 py-2 font-semibold transition-colors ${
                  offeringsSubTab === 'features'
                    ? 'border-b-2'
                    : 'opacity-70 hover:opacity-100'
                }`}
                style={{
                  color: '#0A0A10',
                  borderBottomColor: offeringsSubTab === 'features' ? '#FF6B5A' : 'transparent'
                }}
              >
                Features ({features.length})
              </button>
              <button
                onClick={() => setOfferingsSubTab('collateral-types')}
                className={`px-4 py-2 font-semibold transition-colors ${
                  offeringsSubTab === 'collateral-types'
                    ? 'border-b-2'
                    : 'opacity-70 hover:opacity-100'
                }`}
                style={{
                  color: '#0A0A10',
                  borderBottomColor: offeringsSubTab === 'collateral-types' ? '#FF6B5A' : 'transparent'
                }}
              >
                Collateral Types ({collateralTypes.length})
              </button>
            </div>

            {/* Features Sub-tab */}
            {offeringsSubTab === 'features' && (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-semibold" style={{ color: '#0A0A10' }}>Features</h3>
                  <button
                    onClick={() => {
                      setEditingFeature(null);
                      setFeatureFormData({ title: '', description: '' });
                      setShowFeatureForm(true);
                    }}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-colors"
                    style={{
                      backgroundColor: '#FF6B5A',
                      color: '#FFFFFF'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#ff5540'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#FF6B5A'}
                  >
                    <Plus className="w-4 h-4" />
                    Create Feature
                  </button>
                </div>

                {/* Features Table */}
            <div className="rounded-xl shadow-sm border overflow-hidden" style={{ borderColor: '#D7B472', backgroundColor: '#FFFFFF' }}>
              <table className="w-full">
                <thead style={{ backgroundColor: '#F8F7F4' }}>
                  <tr>
                    <th className="px-6 py-4 text-left font-semibold" style={{ color: '#0A0A10' }}>Title</th>
                    <th className="px-6 py-4 text-left font-semibold" style={{ color: '#0A0A10' }}>Description</th>
                    <th className="px-6 py-4 text-left font-semibold" style={{ color: '#0A0A10' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {features.length === 0 ? (
                    <tr>
                      <td colSpan="3" className="px-6 py-8 text-center" style={{ color: '#0A0A10', opacity: 0.7 }}>
                        No features found
                      </td>
                    </tr>
                  ) : (
                    features.map((feature) => (
                      <tr key={feature.id} className="border-t" style={{ borderColor: '#D7B472' }}>
                        <td className="px-6 py-4 font-medium" style={{ color: '#0A0A10' }}>{feature.value.title}</td>
                        <td className="px-6 py-4" style={{ color: '#0A0A10' }}>{feature.value.description}</td>
                        <td className="px-6 py-4">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleEditFeature(feature)}
                              className="flex items-center gap-1 px-3 py-1.5 rounded transition-colors"
                              style={{
                                backgroundColor: 'rgba(215, 180, 114, 0.15)',
                                color: '#D7B472'
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = 'rgba(215, 180, 114, 0.25)';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'rgba(215, 180, 114, 0.15)';
                              }}
                            >
                              <Edit className="w-4 h-4" />
                              Edit
                            </button>
                            {confirmDeleteFeature === feature.id ? (
                              <div className="flex flex-col gap-2">
                                <div className="text-xs p-2 rounded" style={{ backgroundColor: 'rgba(255, 107, 90, 0.1)', color: '#FF6B5A' }}>
                                  Cannot be undone.
                                </div>
                                <div className="flex items-center gap-2">
                                  <button
                                    onClick={() => handleDeleteFeature(feature.id)}
                                    disabled={deletingFeature}
                                    className="px-3 py-1.5 text-sm rounded transition-colors disabled:opacity-50"
                                    style={{ backgroundColor: '#FF6B5A', color: 'white' }}
                                  >
                                    {deletingFeature ? (
                                      <>
                                        <Loader2 className="w-3 h-3 inline animate-spin mr-1" />
                                        Deleting...
                                      </>
                                    ) : (
                                      'Confirm'
                                    )}
                                  </button>
                                  <button
                                    onClick={() => setConfirmDeleteFeature(null)}
                                    disabled={deletingFeature}
                                    className="px-3 py-1.5 text-sm rounded border transition-colors disabled:opacity-50"
                                    style={{ borderColor: '#D7B472', color: '#0A0A10' }}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <button
                                onClick={() => setConfirmDeleteFeature(feature.id)}
                                className="p-1 rounded hover:bg-gray-200 transition-colors"
                                title="Delete feature"
                              >
                                <Trash2 className="w-4 h-4" style={{ color: '#FF6B5A' }} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
              </div>
            )}

            {/* Collateral Types Sub-tab */}
            {offeringsSubTab === 'collateral-types' && (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-semibold" style={{ color: '#0A0A10' }}>Collateral Types</h3>
                  <button
                    onClick={() => {
                      setEditingCollateralType(null);
                      setCollateralTypeFormData({ title: '', description: '' });
                      setShowCollateralTypeForm(true);
                    }}
                    className="flex items-center gap-2 px-4 py-2 rounded-lg font-semibold transition-colors"
                    style={{
                      backgroundColor: '#FF6B5A',
                      color: '#FFFFFF'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#ff5540'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#FF6B5A'}
                  >
                    <Plus className="w-4 h-4" />
                    Create Collateral Type
                  </button>
                </div>

                {/* Collateral Types Table */}
                <div className="rounded-xl shadow-sm border overflow-hidden" style={{ borderColor: '#D7B472', backgroundColor: '#FFFFFF' }}>
                  <table className="w-full">
                    <thead style={{ backgroundColor: '#F8F7F4' }}>
                      <tr>
                        <th className="px-6 py-4 text-left font-semibold" style={{ color: '#0A0A10' }}>Title</th>
                        <th className="px-6 py-4 text-left font-semibold" style={{ color: '#0A0A10' }}>Description</th>
                        <th className="px-6 py-4 text-left font-semibold" style={{ color: '#0A0A10' }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {collateralTypes.length === 0 ? (
                        <tr>
                          <td colSpan="3" className="px-6 py-8 text-center" style={{ color: '#0A0A10', opacity: 0.7 }}>
                            No collateral types found
                          </td>
                        </tr>
                      ) : (
                        collateralTypes.map((collateralType) => (
                          <tr key={collateralType.id} className="border-t" style={{ borderColor: '#D7B472' }}>
                            <td className="px-6 py-4 font-medium" style={{ color: '#0A0A10' }}>{collateralType.value.title}</td>
                            <td className="px-6 py-4" style={{ color: '#0A0A10' }}>{collateralType.value.description}</td>
                            <td className="px-6 py-4">
                              <div className="flex gap-2">
                                <button
                                  onClick={() => handleEditCollateralType(collateralType)}
                                  className="flex items-center gap-1 px-3 py-1.5 rounded transition-colors"
                                  style={{
                                    backgroundColor: 'rgba(215, 180, 114, 0.15)',
                                    color: '#D7B472'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor = 'rgba(215, 180, 114, 0.25)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = 'rgba(215, 180, 114, 0.15)';
                                  }}
                                >
                                  <Edit className="w-4 h-4" />
                                  Edit
                                </button>
                                {confirmDeleteCollateralType === collateralType.id ? (
                                  <div className="flex flex-col gap-2">
                                    <div className="text-xs p-2 rounded" style={{ backgroundColor: 'rgba(255, 107, 90, 0.1)', color: '#FF6B5A' }}>
                                      Cannot be undone.
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <button
                                        onClick={() => handleDeleteCollateralType(collateralType.id)}
                                        disabled={deletingCollateralType}
                                        className="px-3 py-1.5 text-sm rounded transition-colors disabled:opacity-50"
                                        style={{ backgroundColor: '#FF6B5A', color: 'white' }}
                                      >
                                        {deletingCollateralType ? (
                                          <>
                                            <Loader2 className="w-3 h-3 inline animate-spin mr-1" />
                                            Deleting...
                                          </>
                                        ) : (
                                          'Confirm'
                                        )}
                                      </button>
                                      <button
                                        onClick={() => setConfirmDeleteCollateralType(null)}
                                        disabled={deletingCollateralType}
                                        className="px-3 py-1.5 text-sm rounded border transition-colors disabled:opacity-50"
                                        style={{ borderColor: '#D7B472', color: '#0A0A10' }}
                                      >
                                        Cancel
                                      </button>
                                    </div>
                                  </div>
                                ) : (
                                  <button
                                    onClick={() => setConfirmDeleteCollateralType(collateralType.id)}
                                    className="p-1 rounded hover:bg-gray-200 transition-colors"
                                    title="Delete collateral type"
                                  >
                                    <Trash2 className="w-4 h-4" style={{ color: '#FF6B5A' }} />
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Tenant Form Drawer */}
        <Drawer
          isOpen={showTenantForm}
          onClose={() => {
            setShowTenantForm(false);
            setTenantFormData({ name: '', domain: '' });
          }}
          title="Create Tenant"
          description="Add a new tenant to the system."
        >
          <form className="space-y-4" onSubmit={handleCreateTenant}>
            <div>
              <label className="mb-1 block text-sm font-medium" style={{ color: '#0A0A10' }}>
                Tenant Name
              </label>
              <input
                type="text"
                value={tenantFormData.name}
                onChange={(e) => setTenantFormData({ ...tenantFormData, name: e.target.value })}
                placeholder="Acme Corp"
                className="w-full rounded-lg border px-4 py-2 text-sm focus:outline-none focus:ring-2"
                style={{ borderColor: '#D7B472', color: '#0A0A10', backgroundColor: '#F8F7F4' }}
                onFocus={(e) => { e.currentTarget.style.borderColor = '#D7B472'; e.currentTarget.style.ringColor = '#D7B472'; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = '#D7B472'; }}
                required
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium" style={{ color: '#0A0A10' }}>
                Tenant Domain
              </label>
              <input
                type="text"
                value={tenantFormData.domain}
                onChange={(e) => setTenantFormData({ ...tenantFormData, domain: e.target.value })}
                placeholder="example.com"
                className="w-full rounded-lg border px-4 py-2 text-sm focus:outline-none focus:ring-2"
                style={{ borderColor: '#D7B472', color: '#0A0A10', backgroundColor: '#F8F7F4' }}
                onFocus={(e) => { e.currentTarget.style.borderColor = '#D7B472'; e.currentTarget.style.ringColor = '#D7B472'; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = '#D7B472'; }}
                required
              />
            </div>
            <div className="pt-2">
              <button
                type="submit"
                disabled={creatingTenant}
                className="flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2 text-white transition disabled:cursor-not-allowed"
                style={{ backgroundColor: creatingTenant ? '#D7B472' : '#D7B472' }}
                onMouseEnter={(e) => { if (!creatingTenant) e.currentTarget.style.backgroundColor = '#c5a060'; }}
                onMouseLeave={(e) => { if (!creatingTenant) e.currentTarget.style.backgroundColor = '#D7B472'; }}
              >
                {creatingTenant ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4" />
                    Create Tenant
                  </>
                )}
              </button>
            </div>
          </form>
        </Drawer>

        {/* User Form Drawer */}
        <Drawer
          isOpen={showUserForm}
          onClose={() => {
            setShowUserForm(false);
            setEditingUser(null);
            setUserFormData({
              name: '',
              email: '',
              no_of_allowed_collaterals: '',
              no_of_edit_per_collateral: '',
              tenant_id: '',
              role: 'admin'
            });
          }}
          title={editingUser ? 'Edit User' : 'Create User'}
          description={editingUser ? 'Update user information and settings.' : 'Add a new user to the system.'}
        >
          <form className="space-y-4" onSubmit={editingUser ? handleUpdateUser : handleCreateUser}>
            <div>
              <label className="mb-1 block text-sm font-medium" style={{ color: '#0A0A10' }}>
                User Name
              </label>
              <input
                type="text"
                value={userFormData.name}
                onChange={(e) => setUserFormData({ ...userFormData, name: e.target.value })}
                placeholder="John Doe"
                className="w-full rounded-lg border px-4 py-2 text-sm focus:outline-none focus:ring-2"
                style={{ borderColor: '#D7B472', color: '#0A0A10', backgroundColor: '#F8F7F4' }}
                onFocus={(e) => { e.currentTarget.style.borderColor = '#D7B472'; e.currentTarget.style.ringColor = '#D7B472'; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = '#D7B472'; }}
                required
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium" style={{ color: '#0A0A10' }}>
                Email
              </label>
              <input
                type="email"
                value={userFormData.email}
                onChange={(e) => setUserFormData({ ...userFormData, email: e.target.value })}
                placeholder="john@example.com"
                className="w-full rounded-lg border px-4 py-2 text-sm focus:outline-none focus:ring-2"
                style={{ borderColor: '#D7B472', color: '#0A0A10', backgroundColor: '#F8F7F4' }}
                onFocus={(e) => { e.currentTarget.style.borderColor = '#D7B472'; e.currentTarget.style.ringColor = '#D7B472'; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = '#D7B472'; }}
                required
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium" style={{ color: '#0A0A10' }}>
                Role
              </label>
              <select
                value={userFormData.role}
                onChange={(e) => setUserFormData({ ...userFormData, role: e.target.value })}
                className="w-full rounded-lg border px-4 py-2 text-sm focus:outline-none focus:ring-2"
                style={{ borderColor: '#D7B472', color: '#0A0A10', backgroundColor: '#F8F7F4' }}
                onFocus={(e) => { e.currentTarget.style.borderColor = '#D7B472'; e.currentTarget.style.ringColor = '#D7B472'; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = '#D7B472'; }}
                required
              >
                <option value="user">User</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium" style={{ color: '#0A0A10' }}>
                Tenant
              </label>
              <select
                value={userFormData.tenant_id}
                onChange={(e) => setUserFormData({ ...userFormData, tenant_id: e.target.value })}
                className="w-full rounded-lg border px-4 py-2 text-sm focus:outline-none focus:ring-2"
                style={{ borderColor: '#D7B472', color: '#0A0A10', backgroundColor: '#F8F7F4' }}
                onFocus={(e) => { e.currentTarget.style.borderColor = '#D7B472'; e.currentTarget.style.ringColor = '#D7B472'; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = '#D7B472'; }}
                required
              >
                <option value="">Select a tenant</option>
                {tenants.map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.name} ({tenant.domain})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium" style={{ color: '#0A0A10' }}>
                Allowed Collaterals
              </label>
              <input
                type="number"
                value={userFormData.no_of_allowed_collaterals}
                onChange={(e) => setUserFormData({ ...userFormData, no_of_allowed_collaterals: e.target.value })}
                placeholder="10"
                className="w-full rounded-lg border px-4 py-2 text-sm focus:outline-none focus:ring-2"
                style={{ borderColor: '#D7B472', color: '#0A0A10', backgroundColor: '#F8F7F4' }}
                onFocus={(e) => { e.currentTarget.style.borderColor = '#D7B472'; e.currentTarget.style.ringColor = '#D7B472'; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = '#D7B472'; }}
                required
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium" style={{ color: '#0A0A10' }}>
                Edits per Collateral
              </label>
              <input
                type="number"
                value={userFormData.no_of_edit_per_collateral}
                onChange={(e) => setUserFormData({ ...userFormData, no_of_edit_per_collateral: e.target.value })}
                placeholder="5"
                className="w-full rounded-lg border px-4 py-2 text-sm focus:outline-none focus:ring-2"
                style={{ borderColor: '#D7B472', color: '#0A0A10', backgroundColor: '#F8F7F4' }}
                onFocus={(e) => { e.currentTarget.style.borderColor = '#D7B472'; e.currentTarget.style.ringColor = '#D7B472'; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = '#D7B472'; }}
                required
              />
            </div>
            <div className="pt-2">
              <button
                type="submit"
                disabled={creatingUser || updatingUser}
                className="flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2 text-white transition disabled:cursor-not-allowed"
                style={{ backgroundColor: (creatingUser || updatingUser) ? '#D7B472' : '#D7B472' }}
                onMouseEnter={(e) => { if (!creatingUser && !updatingUser) e.currentTarget.style.backgroundColor = '#c5a060'; }}
                onMouseLeave={(e) => { if (!creatingUser && !updatingUser) e.currentTarget.style.backgroundColor = '#D7B472'; }}
              >
                {(creatingUser || updatingUser) ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {editingUser ? 'Updating...' : 'Creating...'}
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4" />
                    {editingUser ? 'Update User' : 'Create User'}
                  </>
                )}
              </button>
            </div>
          </form>
        </Drawer>

        {/* Feature Form Drawer */}
        <Drawer
          isOpen={showFeatureForm}
          onClose={() => {
            setShowFeatureForm(false);
            setEditingFeature(null);
            setFeatureFormData({ title: '', description: '' });
          }}
          title={editingFeature ? 'Edit Feature' : 'Create Feature'}
          description={editingFeature ? 'Update feature details.' : 'Add a new collateral feature.'}
        >
          <form className="space-y-4" onSubmit={editingFeature ? handleUpdateFeature : handleCreateFeature}>
            <div>
              <label className="mb-1 block text-sm font-medium" style={{ color: '#0A0A10' }}>
                Title
              </label>
              <input
                type="text"
                value={featureFormData.title}
                onChange={(e) => setFeatureFormData({ ...featureFormData, title: e.target.value })}
                placeholder="Feature Title"
                className="w-full rounded-lg border px-4 py-2 text-sm focus:outline-none focus:ring-2"
                style={{ borderColor: '#D7B472', color: '#0A0A10', backgroundColor: '#F8F7F4' }}
                onFocus={(e) => { e.currentTarget.style.borderColor = '#D7B472'; e.currentTarget.style.ringColor = '#D7B472'; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = '#D7B472'; }}
                required
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium" style={{ color: '#0A0A10' }}>
                Description
              </label>
              <textarea
                value={featureFormData.description}
                onChange={(e) => setFeatureFormData({ ...featureFormData, description: e.target.value })}
                placeholder="Feature Description"
                rows={4}
                className="w-full rounded-lg border px-4 py-2 text-sm focus:outline-none focus:ring-2"
                style={{ borderColor: '#D7B472', color: '#0A0A10', backgroundColor: '#F8F7F4' }}
                onFocus={(e) => { e.currentTarget.style.borderColor = '#D7B472'; e.currentTarget.style.ringColor = '#D7B472'; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = '#D7B472'; }}
                required
              />
            </div>
            <div className="pt-2">
              <button
                type="submit"
                disabled={creatingFeature}
                className="flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2 text-white transition disabled:cursor-not-allowed"
                style={{ backgroundColor: '#D7B472' }}
                onMouseEnter={(e) => { if (!creatingFeature) e.currentTarget.style.backgroundColor = '#c5a060'; }}
                onMouseLeave={(e) => { if (!creatingFeature) e.currentTarget.style.backgroundColor = '#D7B472'; }}
              >
                {creatingFeature ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {editingFeature ? 'Updating...' : 'Saving...'}
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4" />
                    {editingFeature ? 'Update Feature' : 'Create Feature'}
                  </>
                )}
              </button>
            </div>
          </form>
        </Drawer>

        {/* Collateral Type Form Drawer */}
        <Drawer
          isOpen={showCollateralTypeForm}
          onClose={() => {
            setShowCollateralTypeForm(false);
            setEditingCollateralType(null);
            setCollateralTypeFormData({ title: '', description: '' });
          }}
          title={editingCollateralType ? 'Edit Collateral Type' : 'Create Collateral Type'}
          description={editingCollateralType ? 'Update collateral type details.' : 'Add a new collateral type.'}
        >
          <form className="space-y-4" onSubmit={editingCollateralType ? handleUpdateCollateralType : handleCreateCollateralType}>
            <div>
              <label className="mb-1 block text-sm font-medium" style={{ color: '#0A0A10' }}>
                Title
              </label>
              <input
                type="text"
                value={collateralTypeFormData.title}
                onChange={(e) => setCollateralTypeFormData({ ...collateralTypeFormData, title: e.target.value })}
                placeholder="Collateral Type Title"
                className="w-full rounded-lg border px-4 py-2 text-sm focus:outline-none focus:ring-2"
                style={{ borderColor: '#D7B472', color: '#0A0A10', backgroundColor: '#F8F7F4' }}
                onFocus={(e) => { e.currentTarget.style.borderColor = '#D7B472'; e.currentTarget.style.ringColor = '#D7B472'; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = '#D7B472'; }}
                required
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium" style={{ color: '#0A0A10' }}>
                Description
              </label>
              <textarea
                value={collateralTypeFormData.description}
                onChange={(e) => setCollateralTypeFormData({ ...collateralTypeFormData, description: e.target.value })}
                placeholder="Collateral Type Description"
                rows={4}
                className="w-full rounded-lg border px-4 py-2 text-sm focus:outline-none focus:ring-2"
                style={{ borderColor: '#D7B472', color: '#0A0A10', backgroundColor: '#F8F7F4' }}
                onFocus={(e) => { e.currentTarget.style.borderColor = '#D7B472'; e.currentTarget.style.ringColor = '#D7B472'; }}
                onBlur={(e) => { e.currentTarget.style.borderColor = '#D7B472'; }}
                required
              />
            </div>
            <div className="pt-2">
              <button
                type="submit"
                disabled={creatingCollateralType}
                className="flex w-full items-center justify-center gap-2 rounded-lg px-4 py-2 text-white transition disabled:cursor-not-allowed"
                style={{ backgroundColor: '#D7B472' }}
                onMouseEnter={(e) => { if (!creatingCollateralType) e.currentTarget.style.backgroundColor = '#c5a060'; }}
                onMouseLeave={(e) => { if (!creatingCollateralType) e.currentTarget.style.backgroundColor = '#D7B472'; }}
              >
                {creatingCollateralType ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {editingCollateralType ? 'Updating...' : 'Saving...'}
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4" />
                    {editingCollateralType ? 'Update Collateral Type' : 'Create Collateral Type'}
                  </>
                )}
              </button>
            </div>
          </form>
        </Drawer>
      </div>
    </div>
  );
}

